/**
 * 前端錯誤日誌工具
 * 用於將前端錯誤發送到後端 PostgreSQL 資料庫
 */

interface LogPayload {
  message: string;
  stack?: string;
  componentStack?: string; // React 專用
  context?: Record<string, unknown>;
  severity?: 'error' | 'warning' | 'info';
}

/**
 * 取得錯誤上下文資訊
 */
function getErrorContext(): Record<string, unknown> {
  const context: Record<string, unknown> = {
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
  };

  // 嘗試從 localStorage 獲取用戶資訊（如果有的話）
  try {
    const token = localStorage.getItem('tenant_token');
    const tenantId = localStorage.getItem('tenant_id');

    if (token) {
      context.hasToken = true;
      // 不儲存完整的 token，只標記有 token
    }
    if (tenantId) {
      context.tenantId = tenantId;
    }
  } catch {
    // localStorage 可能無法訪問（例如在隱私模式下）
    context.localStorageError = '無法訪問 localStorage';
  }

  // 嘗試獲取視窗大小
  try {
    context.windowSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  } catch {
    // 忽略錯誤
  }

  return context;
}

/**
 * 檢查錯誤日誌功能是否啟用
 */
function isErrorLoggingEnabled(): boolean {
  // 從環境變數讀取，預設為 true（啟用）
  const envValue = import.meta.env.VITE_ENABLE_ERROR_LOGGING;
  // 如果明確設定為 'false'，則關閉；否則啟用
  return envValue !== 'false';
}

/**
 * 發送錯誤到伺服器
 * @param error - Error 物件或錯誤訊息字串
 * @param extraInfo - 額外資訊（context, componentStack, severity）
 */
export const logErrorToServer = async (
  error: Error | string,
  extraInfo: Partial<LogPayload> = {}
): Promise<void> => {
  // 檢查錯誤日誌功能是否啟用
  const isEnabled = isErrorLoggingEnabled();

  // 在開發環境中顯示詳細的調試信息
  if (import.meta.env.DEV) {
    console.log('[Logger] 錯誤日誌功能狀態:', {
      isEnabled,
      envValue: import.meta.env.VITE_ENABLE_ERROR_LOGGING,
      apiBase: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787',
    });
  }

  if (!isEnabled) {
    // 功能已關閉，只在開發環境中顯示 Console 錯誤
    if (import.meta.env.DEV) {
      console.error('[Logger] 錯誤日誌功能已關閉');
    }
    return;
  }

  // 1. 本地開發時，仍然要在 Console 顯示，方便除錯
  if (import.meta.env.DEV) {
    console.error('[Logger] 準備發送錯誤日誌:', error, extraInfo);
  }

  // 2. 準備 Payload
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  // 合併上下文資訊
  const baseContext = getErrorContext();
  const mergedContext = {
    ...baseContext,
    ...extraInfo.context, // 合併額外傳入的 context
  };

  const payload = {
    severity: extraInfo.severity || 'error',
    message,
    stack_trace: stack,
    component_stack: extraInfo.componentStack,
    context: mergedContext,
    user_agent: navigator.userAgent,
  };

  try {
    // 3. 使用 fetch + keepalive 確保在頁面關閉前也能傳送
    const apiBase =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    const apiUrl = `${apiBase}/api/logs/error`;

    // 在開發環境中顯示發送詳情
    if (import.meta.env.DEV) {
      console.log('[Logger] 發送錯誤日誌到:', apiUrl);
      console.log('[Logger] Payload:', payload);
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true, // 關鍵：允許請求在頁面卸載後繼續完成
    });

    // 檢查回應狀態
    if (!response.ok) {
      // 如果回應不是 200，記錄警告但不拋出異常
      const responseText = await response
        .text()
        .catch(() => '無法讀取回應內容');
      console.error('[Logger] 錯誤日誌發送失敗:', {
        status: response.status,
        statusText: response.statusText,
        url: apiUrl,
        response: responseText.substring(0, 500),
      });

      // 如果是 404，提示可能需要部署
      if (response.status === 404 && import.meta.env.DEV) {
        console.error(
          '[Logger] ⚠️  API 路由不存在 (404)，可能需要部署後端代碼'
        );
      }
    } else {
      // 在開發環境中顯示成功回應
      if (import.meta.env.DEV) {
        const responseData = await response
          .json()
          .catch(() => ({ message: '無法解析 JSON 回應' }));
        console.log('[Logger] ✅ 錯誤日誌發送成功:', {
          status: response.status,
          statusText: response.statusText,
          data: responseData,
        });
      }
    }
  } catch (sendError) {
    // 萬一 Log Server 掛了，降級處理，不要讓 App 崩潰
    // 但在所有環境都記錄錯誤，方便排查
    console.error('[Logger] ❌ 無法發送錯誤日誌到伺服器:', {
      error: sendError instanceof Error ? sendError.message : String(sendError),
      stack: sendError instanceof Error ? sendError.stack : undefined,
      apiUrl: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'}/api/logs/error`,
    });
  }
};

/**
 * 記錄警告到伺服器
 */
export const logWarningToServer = async (
  message: string,
  context?: Record<string, unknown>
): Promise<void> => {
  await logErrorToServer(message, {
    severity: 'warning',
    context,
  });
};

/**
 * 記錄資訊到伺服器
 */
export const logInfoToServer = async (
  message: string,
  context?: Record<string, unknown>
): Promise<void> => {
  await logErrorToServer(message, {
    severity: 'info',
    context,
  });
};

/**
 * 測試錯誤日誌功能（開發環境專用）
 */
declare global {
  interface Window {
    testErrorLog?: () => Promise<void>;
  }
}
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.testErrorLog = async () => {
    console.log('🧪 測試錯誤日誌功能...');
    await logErrorToServer(new Error('手動測試錯誤'), {
      context: { test: true, timestamp: new Date().toISOString() },
    });
    console.log('✅ 測試錯誤已發送，請檢查：');
    console.log('  1. 瀏覽器 Network 標籤中的 /api/logs/error 請求');
    console.log('  2. 後端終端中的 [Error Log] 日誌');
    console.log('  3. 資料庫中的 app_error_logs 表');
  };
}
